import { useEffect, useRef, useState } from "react";

/**
 * Overlay flotante circular con un video del avatar de Papi hablando.
 * Los videos de D-ID vienen con AUDIO embebido, así que no hace falta
 * reproducir el MP3 por separado — el video mismo trae la voz.
 *
 * Props:
 *   - videoUrl: string | null — URL del MP4 pregenerado
 *   - onEnded: fn opcional — cuando el video termina naturalmente
 *   - onFallback: fn opcional — si el video no carga (404), llamado UNA vez
 *   - size: "sm"|"md"|"lg" — 100/140/200 px de diámetro
 *
 * Autoplay: si el usuario acaba de hacer un tap (login, ver receta), iOS
 * permite play sonoro. Si el navegador bloquea, hacemos fallback silencioso
 * y llamamos onFallback para que el caller reproduzca el MP3 tradicional.
 */
export default function AvatarOverlay({ videoUrl, onEnded, onFallback, size = "md" }) {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);
  const dims = { sm: 100, md: 140, lg: 200 }[size] || 140;

  useEffect(() => {
    setVisible(false);
    setFailed(false);
    if (!videoUrl) return;
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        // Autoplay bloqueado → informar al caller para que use audio-only fallback
        setFailed(true);
        onFallback?.();
      });
    }
  }, [videoUrl, onFallback]);

  if (!videoUrl || failed) return null;

  return (
    <div
      data-testid="avatar-overlay"
      className={`fixed z-[60] transition-all duration-500 ease-out ${visible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
      style={{
        right: "1.2rem",
        bottom: "calc(env(safe-area-inset-bottom, 0) + 1.2rem)",
        width: dims,
        height: dims,
      }}
    >
      <div className="relative w-full h-full rounded-full overflow-hidden shadow-2xl ring-4 ring-amber-deep/40 bg-cocoa/10">
        <video
          ref={videoRef}
          src={videoUrl}
          playsInline
          preload="auto"
          className="w-full h-full object-cover scale-110"
          onCanPlay={() => setVisible(true)}
          onError={() => { setFailed(true); onFallback?.(); }}
          onEnded={() => { setVisible(false); setTimeout(() => onEnded?.(), 500); }}
        />
      </div>
    </div>
  );
}
